import React, { ReactNode, createContext, useContext } from 'react'
import { AdvisorData } from '../utils/types'
import { readLocalStorageJson, readSessionStorage } from '../utils/safeStorage'

declare global {
  interface Window {
    matches?: AdvisorData[]
    currentSavings?: string
    // Savings band the session was matched at (lead-engine matched_savings),
    // injected by SAapp. Empty string when unavailable.
    matchedSavings?: string
    // Allowlist-filtered funnel-answer map for personalization's `answers` rule gate, injected
    // by SAapp. May be absent (older SAapp, or the standalone dev page).
    funnelAnswers?: Record<string, string[]>
  }
}

interface MatchScreenState {
  matches: Window['matches']
  currentSavings: Window['currentSavings']
  matchedSavings: Window['matchedSavings']
  funnelAnswers: Window['funnelAnswers']
  pageKey: string
}

export interface MatchScreenDatasetAttributes {
  sessionUUID: string
  revisit: boolean
  selfSchedulingEnabled: boolean
  // Admin advisor-preview render; suppresses lead-gen side effects
  isPreview: boolean
}

export interface postResponse {
  questionName: string
  answer: string
}

interface RetargetStore {
  storedAnswers: postResponse[] | []
}

interface MatchScreenContextValue {
  state: MatchScreenState & MatchScreenDatasetAttributes & RetargetStore
  dynamicTargeting: Record<string, string | string[]>
}

interface MatchScreenProviderProps {
  attributes: MatchScreenDatasetAttributes
  children: ReactNode
}

const MatchScreenContext =
    createContext<MatchScreenContextValue | undefined>(undefined)

const readStoredAnswers = (): postResponse[] => {
  const stored = readLocalStorageJson('retargetStore')
  if (!Array.isArray(stored)) return []
  return stored.filter((entry): entry is postResponse =>
    entry != null && typeof entry === 'object' && typeof entry.questionName === 'string')
}

export function MatchScreenProvider ({ attributes, children }: MatchScreenProviderProps) {
  const contextValue: MatchScreenContextValue = {
    state: {
      matches: window.matches,
      currentSavings: window.currentSavings,
      matchedSavings: window.matchedSavings,
      funnelAnswers: window.funnelAnswers,
      pageKey: 'matchScreen',
      sessionUUID: attributes.sessionUUID,
      revisit: attributes.revisit,
      selfSchedulingEnabled: attributes.selfSchedulingEnabled,
      isPreview: attributes.isPreview,
      storedAnswers: readStoredAnswers()
    },
    dynamicTargeting: {
      uid: readSessionStorage('session_uuid') || '',
      ref: document.referrer ? new URL(document.referrer).hostname : ''
    }
  }

  return <MatchScreenContext.Provider value={contextValue}>
      {children}
  </MatchScreenContext.Provider>
}

export function useMatchScreen (): MatchScreenContextValue {
  const context = useContext(MatchScreenContext)
  if (!context) {
    throw new Error('useMatchScreen must be used within a MatchScreenProvider')
  }
  return context
}
